import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  cardHolderNameRegex,
  // cardNumberRegExp,
  // cvvRegex,
  dateRegex,
  doLuhnCheck,
  isFutureDate,
} from '~/utils/validateFields';
export const numberRegExp = /[0-9]/;

export const useYupValidators = () => {
  const { t } = useTranslation();
  const limitAccountNumber = useCallback(
    () => localStorage.getItem('limitAccountNumber') ?? (10 as any),
    [localStorage.getItem('limitAccountNumber')],
  );
  // console.log(localStorage.getItem('limitAccountNumber'), 'limitAccountNumber');
  // const limitAccountNumber = parseInt(localStorage.getItem('limitAccountNumber'));
  const phoneValidationSchema = yup.string().test('is-valid-phone', t('numberInvalidForCountry'), function (value) {
    const { path, createError } = this;
    const indicatif = localStorage.getItem('countrySelectedOne');
    let maxLength = 9; // Valeur par défaut
    const minLength = 8; // Pour Tchad et RCA

    if (!value || !indicatif) {
      return true; // Autoriser le champ vide ou sans indicatif
    }

    // On définit la longueur minimale et maximale selon l'indicatif
    switch (indicatif) {
      case '225': // Côte d'Ivoire
        maxLength = 10;
        break;
      case '235': // Tchad
      case '236': // RCA
        maxLength = 8;
        break;
      case '237': // Cameroun
      case '241': // Gabon
      case '242': // Congo
        maxLength = 9;
        break;
      default:
        return createError({
          path,
          message: t('indicatifUnknown'),
        });
    }

    // Validation de la longueur du numéro de téléphone
    const numericValue = value.replace(/\D/g, ''); // Supprimer les caractères non numériques

    switch (indicatif) {
      case '225': // Côte d'Ivoire
        if (numericValue.length < 10 || numericValue.length > 10) {
          return createError({
            path,
            message: t('expressUnion:numberMustContain', { maxLength }),
          });
        }
        break;
      case '235': // Tchad
      case '236': // RCA
        if (numericValue.length < 8 || numericValue.length > 8) {
          return createError({
            path,
            message: t('expressUnion:numberMustContain', { minLength }),
            // message: `Le numéro doit contenir ${maxLength} chiffres.`,
          });
        }
        break;
      case '237': // Cameroun
      case '241': // Gabon
      case '242': // Congo
        if (numericValue.length < 9 || numericValue.length > 9) {
          return createError({
            path,
            message: t('expressUnion:numberMustContain', { maxLength }),
            // message: `Le numéro doit contenir ${maxLength} chiffres.`,
          });
        }
        break;
      default:
        return createError({
          path,
          message: t('indicatifUnknown'),
        });
    }

    // if (numericValue.length < minLength || numericValue.length > maxLength) {
    //   return createError({
    //     path,
    //     message: t('expressUnion:numberMustContain', { maxLength }),
    //     // message: `Le numéro doit contenir ${maxLength} chiffres.`,
    //   });
    // }

    return true; // Numéro valide
  });

  const yupValidators = {
    email: yup
      .string()
      .trim()
      .email(t('defaultValidationErrors:enterValidEmail'))
      .required(t('defaultValidationErrors:emailFieldIsRequired'))
      .min(5, t('defaultValidationErrors:emailMinLengthErrorMessage', { min: 5 }))
      .max(128, t('defaultValidationErrors:emailMaxLengthErrorMessage', { max: 128 })),
    genericRequiredString: ({ message, min = 0, max = Infinity }: { message?: string; min?: number; max?: number }) =>
      yup
        .string()
        .trim()
        .required(message || t('defaultValidationErrors:thisFieldIsRequired'))
        .min(min, t('defaultValidationErrors:minLengthErrorMessage', { min }))
        .max(max, t('defaultValidationErrors:maxLengthErrorMessage', { max }))
        .nullable(),
    genericRequiredNumber: ({
      message,
      min = 0,
      max = Infinity,
      matches,
    }: {
      message?: string;
      min?: number;
      max?: number;
      matches?: string;
    }) =>
      yup
        .string()
        .trim()
        .required(message || t('defaultValidationErrors:thisFieldIsRequired'))
        .matches(numberRegExp, matches || t('defaultValidationErrors:invalidFieldValue'))
        .min(min, t('defaultValidationErrors:minLengthErrorMessage', { min }))
        .max(max, t('defaultValidationErrors:maxLengthErrorMessage', { max })),
    cardNumber: yup
      .string()
      .required(t('home:cardPaymentForm:cardNumberRequired'))
      // .matches(cardNumberRegExp, t('home:cardPaymentForm:incorrectCardNumber'))
      .test('cardNumber', t('home:cardPaymentForm:incorrectCard'), (value: any) => doLuhnCheck(value ?? '')),
    expirationDate: yup
      .string()
      .required(t('home:cardPaymentForm:expiryDateRequired'))
      .matches(dateRegex, t('home:cardPaymentForm:invalidExpiryDate'))
      .test('expirationDate', t('home:cardPaymentForm:invalidExpiryDate'), (value: any) => isFutureDate(value ?? '')),
    cvv: yup
      .string()
      .required(t('home:cardPaymentForm:cvvCodeRequired'))
      .matches(numberRegExp, t('home:cardPaymentForm:incorrecCvvCode')),
    cardHolder: yup
      .string()
      .required(t('home:cardPaymentForm:cardHolderNameRequired'))
      .matches(cardHolderNameRegex, t('home:cardPaymentForm:cardHolderIncorrect')),
    phoneNumber: yup.string().required(t('home:cardPaymentForm:phoneNumber')),
    dateOfBirth: yup
      .string()
      .required(t('bank:dateOfBirthRequired'))
      .matches(/^\d{4}-\d{2}-\d{2}$/, t('bank:invalidDateOfBirthFormat')),
    // bankAccount: yup.string().required(t('bank:bankAccountNumber')).matches(/^\d+$/, t('bank:invalidFieldValue')),
    bankAccount: yup
      .string()
      .required(t('bank:bankAccountNumber'))
      .max(limitAccountNumber(), t('bank:bankAccountNumberMaxLengthErrorMessage', { max: limitAccountNumber() }))
      .matches(/^[a-zA-Z0-9]+$/, t('bank:invalidFieldValue')),
    bankName: yup.string().required(t('bank:bankNameRequired')),
    otp: yup.string().required(t('otpForm:otpCodeRequired')),
    indicatifExpressUnion: yup.string().required(t('expressUnion:indicatifExpressUnionRequired')),
    // numberExpressUnion: yup.string().matches(numberRegExp).required(t('expressUnion:numberExpressUnionRequired')),
    numberExpressUnion: phoneValidationSchema,
  };
  return yupValidators;
};
